/**
 * Fires `form_lp_start` on the visitor's FIRST interaction with a
 * DESIGNATED lead-capture form on the page (focus or first input), once
 * per pageview. Feeds a live PostHog HTTP-webhook destination -> Cortana
 * conversion ("LP Form Start").
 *
 * Scoping is explicit opt-in, NOT sitewide. A form is designated when:
 *   1. It carries `data-track-form="lp"` (preferred; add in HTML or
 *      Webflow Designer -> Element Settings -> Custom Attributes), OR
 *   2. It matches one of the selectors in `IgnitionPixelConfig.forms[]`.
 *      NOTE: the submit handler in submit.ts uses `forms[]` as
 *      per-form config PLUS a fallback that matches every form on the
 *      page (see matchForm in submit.ts). This handler intentionally
 *      does NOT use that fallback -- for form_lp_start, `forms[]` is a
 *      strict opt-in list. Non-listed forms without the data attribute
 *      never fire the event.
 *
 * Contact-us, newsletter, footer, and any other non-LP form on the same
 * site stay dark unless they carry the data attribute or are explicitly
 * listed in `forms[]`.
 *
 * Once-per-pageview is enforced via a shared `window` flag (see
 * CROSS_BUNDLE_FIRED_KEY below), NOT a per-form guard. The conversion
 * action is a page-level intent signal ("visitor began engaging with a
 * lead form") -- duplicating it across forms on the same page would
 * inflate the count. The single first designated form the visitor
 * touches wins. The shared flag also dedupes across bundles: the pixel
 * and the flow-widget both check + set the same key, so a page hosting
 * both surfaces still produces a single capture.
 *
 * Listener is attached at `document` in the capture phase so it catches
 * events even when nested handlers `stopPropagation()`. Focus/input events
 * inside Shadow DOM do NOT bubble across the shadow boundary -- widgets
 * like `<ignition-flow>` are responsible for firing this event themselves
 * (see packages/flow-widget/src/app.tsx).
 */

import type { PixelConfig, PixelFormConfig } from './config';
import { posthogCapture, posthogCaptureReady } from './posthog';
import { uuidv4 } from './uuid';

const OPT_IN_ATTRIBUTE = 'data-track-form';
const OPT_IN_VALUE = 'lp';
// Shared cross-bundle guard. The pixel and the flow-widget both import
// (or duplicate) this constant to check + set the same `window` flag, so
// a visitor who touches a designated Webflow form AND the widget on the
// same page only produces one form_lp_start. A module-level `fired`
// boolean alone is insufficient because the two bundles are compiled
// independently and share no module-level state at runtime.
const CROSS_BUNDLE_FIRED_KEY = '__ignFormLpStartFired';

interface CrossBundleWindow {
  [CROSS_BUNDLE_FIRED_KEY]?: boolean;
}

function isCrossBundleFired(): boolean {
  if (typeof window === 'undefined') return false;
  return (window as unknown as CrossBundleWindow)[CROSS_BUNDLE_FIRED_KEY] === true;
}

function markCrossBundleFired(): void {
  if (typeof window === 'undefined') return;
  (window as unknown as CrossBundleWindow)[CROSS_BUNDLE_FIRED_KEY] = true;
}

interface FormStartRuntime {
  config: PixelConfig;
}

export function installFormStartHandler(runtime: FormStartRuntime): () => void {
  if (typeof document === 'undefined') return () => undefined;
  const handler = (e: Event): void => {
    if (isCrossBundleFired()) return;
    // Cheap early exit BEFORE resolving forms + building the payload.
    // Focus/input are hot paths; skip uuidv4() and URL parsing when
    // posthog is unreachable. The cross-bundle flag stays unset so a
    // later interaction after posthog loads still fires.
    if (!posthogCaptureReady()) return;
    const form = resolveForm(e.target);
    if (!form) return;
    const scoped = matchDesignatedForm(form, runtime.config.forms);
    if (!scoped) return;
    // Only latch the cross-bundle flag when the capture was actually
    // dispatched. posthogCapture returns false on a throw or a race
    // where the stub disappeared between the reachability check and
    // the call; keeping the flag gated on the return value preserves
    // retry semantics for that edge case too.
    const dispatched = posthogCapture('form_lp_start', {
      form_id: scoped.formId,
      pathname: safePathname(),
      page_url: safePageUrl(),
      $insert_id: uuidv4(),
    });
    if (dispatched) markCrossBundleFired();
  };
  // Capture phase so nested stopPropagation() cannot swallow the event.
  // `focusin` (not `focus`) because `focus` does not bubble and we listen
  // at `document`.
  document.addEventListener('focusin', handler, true);
  document.addEventListener('input', handler, true);
  return () => {
    document.removeEventListener('focusin', handler, true);
    document.removeEventListener('input', handler, true);
  };
}

/**
 * Returns the enclosing <form> if the event target is a form control
 * (input / textarea / select / button) inside a form, or the form itself
 * if the event target IS a form (e.g. someone tabs to the form container).
 * Returns undefined otherwise so incidental focus/input on non-form
 * elements does not fire the start event.
 */
function resolveForm(target: EventTarget | null): HTMLFormElement | undefined {
  if (!target || !(target instanceof Element)) return undefined;
  if (target instanceof HTMLFormElement) return target;
  const form = target.closest('form');
  return form instanceof HTMLFormElement ? form : undefined;
}

/**
 * Returns a resolved `{ formId }` when the form is designated for
 * form_lp_start firing (via data-track-form attribute OR selector match
 * against config.forms), or undefined for non-designated forms. Does NOT
 * fall back to slugifying data-name/id like `matchForm` in submit.ts --
 * the submit handler intentionally catches every form for the POST + POST-
 * only integrations, but form_lp_start is an ad-conversion signal that
 * must stay scoped to paid-LP intent.
 */
function matchDesignatedForm(
  form: HTMLFormElement,
  forms: PixelFormConfig[]
): { formId: string } | undefined {
  // Priority 1: explicit opt-in via HTML/Webflow attribute.
  const attrValue = form.getAttribute(OPT_IN_ATTRIBUTE);
  if (attrValue && attrValue.trim().toLowerCase() === OPT_IN_VALUE) {
    const rawName = form.getAttribute('data-name') || form.id || '';
    const formId = rawName ? slugifyPart(rawName) : 'lp-form';
    return { formId };
  }
  // Priority 2: selector match against the paid-LP allowlist.
  for (const f of forms) {
    try {
      if (form.matches(f.selector)) return { formId: f.formId };
    } catch {
      /* invalid selector */
    }
  }
  return undefined;
}

// Kept private + minimal on purpose so form_lp_start does not import from
// submit.ts (avoids the temptation to reuse `matchForm`, which by design
// falls back to a synthetic formId for ANY form -- exactly what this
// handler must NOT do).
function slugifyPart(raw: string): string {
  return (
    raw
      .toLowerCase()
      .trim()
      .replace(/_/g, '-')
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '') || 'lp-form'
  );
}

function safePageUrl(): string | undefined {
  try {
    if (typeof window === 'undefined' || !window.location) return undefined;
    const u = new URL(window.location.href);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return undefined;
    return u.origin + u.pathname;
  } catch {
    return undefined;
  }
}

function safePathname(): string | undefined {
  try {
    if (typeof window === 'undefined' || !window.location) return undefined;
    const p = window.location.pathname;
    return typeof p === 'string' && p ? p : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Reset the cross-bundle `fired` flag. Intended for tests only -- production
 * pages have exactly one pageview per guard reset, so the once-guard needs
 * no reset in real usage. Clears the shared `window` flag so tests within
 * a single Vitest worker do not become order-dependent.
 */
export function __resetFormStartFiredForTests(): void {
  if (typeof window === 'undefined') return;
  delete (window as unknown as CrossBundleWindow)[CROSS_BUNDLE_FIRED_KEY];
}
